import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faSave,
  faTimes,
  faCamera,
  faExclamationTriangle,
  faUser,
  faPhone,
  faEnvelope,
  faInfoCircle,
  faRotateRight,
  faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  InputGroup,
  Alert,
  Modal,
} from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";

import ProfilePicture from "../../../assets/img/team/profile-picture-3.jpg";
import { userProfileData, userUpdateProfile, verifyBankByPennyDrop } from "../../../pages/api";
import { MEDIA_URL } from "../../helper";

/* ---------- constants remain unchanged ---------- */


const STATE_OPTIONS = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const CITY_OPTIONS = {
  Maharashtra: ["Mumbai", "Pune", "Nagpur", "Nashik"],
  Karnataka: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi"],
  Gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot"],
  Delhi: ["New Delhi"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Varanasi"],
};

const KYC_STATUS_VARIANT = (status) => {
  const key = String(status || "").toUpperCase();

  const MAP = {
    VERIFIED: "success",
    DONE: "success",
    PENDING: "warning",
    REJECTED: "danger",
  };

  return MAP[key] || "secondary";
};

const maskMobileNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 10) return value || "N/A" || "-";
  return `${digits.slice(0, 2)}******${digits.slice(-2)}`;
};

const maskAadhaarNumber = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "N/A" || "-";
  if (raw.includes("X")) return raw;
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 4) return raw;
  return `XXXX XXXX ${digits.slice(-4)}`;
};

const maskPanNumber = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "N/A" || "-";
  if (raw.includes("X")) return raw;
  if (raw.length < 4) return raw;
  return `XXXXXX${raw.slice(-4)}`;
};

const maskAccountNumber = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "N/A" || "-";
  if (raw.includes("X")) return raw;
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 4) return raw;
  return `XXXXXXXX${digits.slice(-4)}`;
};

const maskIfscCode = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "N/A" || "-";
  if (raw.includes("X")) return raw;
  if (raw.length <= 4) return raw;
  return `${raw.slice(0, 4)}XXXXXX`;
};

const normalizeFileUrl = (path) => {
  if (!path) return ProfilePicture;
  const value = String(path).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${MEDIA_URL}/${value.replace(/^\/+/, "")}`.replace(
    /([^:]\/)\/+/g,
    "$1"
  );
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_KYC_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_PROFILE_PHOTO_MIME_TYPES = ["image/jpeg", "image/png"];

const initialProfile = {
  id: null,
  profilePhoto: null,
  fullName: "Bonnie Green",
  referralNumber: "REF123456",
  mobileNumber: "9876543210",
  emailId: "Bonnie@example.com",
  addressLine1: "Flat 12, Shree Residency",
  addressLine2: "Near Main Market",
  state: "Maharashtra",
  city: "Pune",
  pincode: "416012",
  kycStatus: "DONE",
  panNumber: "ABCDE1234F",
  aadhaarNumber: "123412341234",
  idProofName: "aadhaar-card.pdf",
  idProofPath: "",
  bankAccountHolderName: "Bonnie Green",
  bankName: "State Bank of India",
  accountNumber: "123456789012",
  ifscCode: "SBIN0001234",
};

const normalizeKycStatus = (value) =>
  String(value || "PENDING").trim().toUpperCase();

const getFileNameFromPath = (path) => {
  if (!path) return "";
  const parts = String(path).split("/");
  return parts[parts.length - 1] || "";
};

const getResolvedImageSrc = (path) => {
  if (!path) return ProfilePicture;
  if (typeof path !== "string") return ProfilePicture;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  return normalizeFileUrl(path);
};

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};


const mapApiProfileToState = (data = {}) => {
  const bankDetails = data?.bank_details || {};
  const mobileNumber = String(data?.mobile_number || "").replace(/\D/g, "");

  return {
    id: data?.id ?? null,
    profilePhoto: data?.profile_photo || null,
    fullName: data?.name || "",
    referralNumber: data?.referral_number || "",
    mobileNumber: mobileNumber.length === 10 ? mobileNumber : "",
    emailId: data?.email || "",
    addressLine1: data?.address_line_1 || "",
    addressLine2: data?.address_line_2 || "",
    state: data?.state || "",
    city: data?.city || "",
    pincode: String(data?.pincode || ""),
    kycStatus: normalizeKycStatus(data?.kyc_status),
    panNumber: data?.pan_number || "",
    aadhaarNumber: data?.aadhar_number || "",
    idProofName: getFileNameFromPath(data?.id_proof) || "",
    idProofPath: data?.id_proof || null,
    bankAccountHolderName: bankDetails?.account_holder_name || "",
    bankName: bankDetails?.bank_name || "",
    accountNumber: bankDetails?.account_number || "",
    ifscCode: bankDetails?.ifsc_code || "",
    customUserId: data?.custom_user_id || "N/A",
    // businessGenerated: data?.business_generated ?? "0",
    totalEarnings: data?.total_earning ?? "0",
    // level: data?.level ?? "N/A",
    walletBalance: data?.wallet_balance ?? "0",
  };
};

const createCroppedImageFile = (imageSrc, originalFile, zoom, rotation) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const outputSize = 600;
        canvas.width = outputSize;
        canvas.height = outputSize;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Unable to create crop canvas."));
          return;
        }

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, outputSize, outputSize);

        ctx.translate(outputSize / 2, outputSize / 2);
        ctx.rotate((rotation * Math.PI) / 180);

        const scale = Math.max(outputSize / image.width, outputSize / image.height) * zoom;
        ctx.scale(scale, scale);
        ctx.drawImage(image, -image.width / 2, -image.height / 2);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Unable to create cropped image."));
              return;
            }

            const file = new File([blob], originalFile.name, {
              type: originalFile.type || "image/png",
            });

            resolve(file);
          },
          originalFile.type || "image/png",
          0.95
        );
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error("Unable to load image for cropping."));
    image.src = imageSrc;
  });
};

export default function UserProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [draft, setDraft] = useState(initialProfile);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [profilePreview, setProfilePreview] = useState(ProfilePicture);
  const [profileImageError, setProfileImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authUser = JSON.parse(sessionStorage.getItem("authUser") || "{}");
  const kycId = Number(authUser?.kyc_id);

  const [showCropModal, setShowCropModal] = useState(false);
  const [cropSourceFile, setCropSourceFile] = useState(null);
  const [cropSourcePreview, setCropSourcePreview] = useState("");
  const [cropZoom, setCropZoom] = useState(1);
  const [cropRotation, setCropRotation] = useState(0);
  const [cropping, setCropping] = useState(false);

  const [isBankEditing, setIsBankEditing] = useState(false);
  const [bankVerifying, setBankVerifying] = useState(false);

  const profilePhotoInputRef = useRef(null);

  const selectedCities = useMemo(() => CITY_OPTIONS[draft.state] || [], [draft.state]);

  const hasKycSensitiveNameChange = useMemo(() => {
    return (
      profile.fullName.trim() !== draft.fullName.trim() &&
      profile.kycStatus === "DONE"
    );
  }, [profile.fullName, profile.kycStatus, draft.fullName]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await userProfileData();
        const data = response?.data?.data || response?.data || {};
        const mappedProfile = mapApiProfileToState(data);

        setProfile(mappedProfile);
        setProfileImageError(false);
        setDraft(mappedProfile);
        setProfilePreview(getResolvedImageSrc(mappedProfile.profilePhoto));
      } catch (error) {
        // console.error("Failed to fetch profile:", error);
        toast.error(error?.response?.data?.message || "Unable to fetch profile details.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    return () => {
      if (cropSourcePreview && cropSourcePreview.startsWith("blob:")) {
        URL.revokeObjectURL(cropSourcePreview);
      }
    };
  }, [cropSourcePreview]);

  const openEditMode = () => {
    setDraft(profile);
    setErrors({});
    setAlertMessage("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(profile);
    setErrors({});
    setAlertMessage("");
    setIsEditing(false);
    setProfilePreview(getResolvedImageSrc(profile.profilePhoto));
  };

  const closeCropModal = () => {
    if (cropSourcePreview && cropSourcePreview.startsWith("blob:")) {
      URL.revokeObjectURL(cropSourcePreview);
    }

    setShowCropModal(false);
    setCropSourceFile(null);
    setCropSourcePreview("");
    setCropZoom(1);
    setCropRotation(0);

    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = "";
    }
  };

  /* ---------- input handlers unchanged ---------- */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setDraft((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" ? { city: "" } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleMobileChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setDraft((prev) => ({ ...prev, mobileNumber: nextValue }));
    setErrors((prev) => ({ ...prev, mobileNumber: "" }));
  };

  const handlePincodeChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    setDraft((prev) => ({ ...prev, pincode: nextValue }));
    setErrors((prev) => ({ ...prev, pincode: "" }));
  };

  const handleAadhaarChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 12);
    setDraft((prev) => ({ ...prev, aadhaarNumber: nextValue }));
    setErrors((prev) => ({ ...prev, aadhaarNumber: "" }));
  };

  const handleAccountNumberChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 18);
    setDraft((prev) => ({ ...prev, accountNumber: nextValue }));
    setErrors((prev) => ({ ...prev, accountNumber: "" }));
  };

  /* ---------- file validation ---------- */

  const validateFile = (file) => {
    if (!file) return "Please choose a file.";
    if (!ALLOWED_KYC_MIME_TYPES.includes(file.type)) {
      return "Only PDF, JPG, and PNG files are allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be 2 MB or less.";
    }
    return "";
  };

  const validateProfilePhotoFile = (file) => {
    if (!file) return "Please choose a file.";
    if (!ALLOWED_PROFILE_PHOTO_MIME_TYPES.includes(file.type)) {
      return "Only JPG and PNG images are allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be 2 MB or less.";
    }
    return "";
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateProfilePhotoFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, profilePhoto: validationError }));
      toast.error(validationError);
      if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = "";
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setProfileImageError(false);
    setProfilePreview(previewUrl);
    setDraft((prev) => ({
      ...prev,
      profilePhoto: file,
    }));
    setErrors((prev) => ({ ...prev, profilePhoto: "" }));
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, idProof: validationError }));
      toast.error(validationError);
      return;
    }

    setDraft((prev) => ({
      ...prev,
      idProofFile: file,
      idProofName: file.name,
    }));
    setErrors((prev) => ({ ...prev, idProof: "" }));
  };

  const applyCroppedProfilePhoto = async () => {
    if (!cropSourceFile || !cropSourcePreview) {
      toast.error("No image selected for cropping.");
      return;
    }

    try {
      setCropping(true);
      const croppedFile = await createCroppedImageFile(
        cropSourcePreview,
        cropSourceFile,
        cropZoom,
        cropRotation
      );

      const croppedPreviewUrl = URL.createObjectURL(croppedFile);

      setProfilePreview(croppedPreviewUrl);
      setDraft((prev) => ({
        ...prev,
        profilePhoto: croppedFile,
      }));
      setErrors((prev) => ({ ...prev, profilePhoto: "" }));
      closeCropModal();
    } catch (error) {
      // console.error("Cropping failed:", error);
      toast.error(error?.message || "Unable to crop profile image.");
    } finally {
      setCropping(false);
    }
  };

  /* ---------- validation + save ---------- */

  const validate = () => {
    const nextErrors = {};

    if (!draft.fullName.trim()) nextErrors.fullName = "Full name is required.";

    if (!draft.mobileNumber.trim()) {
      nextErrors.mobileNumber = "Mobile number is required.";
    } else if (!/^[6-9]\d{9}$/.test(draft.mobileNumber)) {
      nextErrors.mobileNumber = "Enter a valid 10-digit mobile number.";
    }

    if (!draft.emailId.trim()) {
      nextErrors.emailId = "Email ID is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildUpdatePayload = () => {
    const payload = new FormData();

    payload.append("name", draft.fullName || "");
    payload.append(
      "mobile_number",
      String(draft.mobileNumber || "").replace(/\D/g, "").slice(0, 10)
    );
    payload.append("email", draft.emailId || "");
    payload.append("address_line_1", draft.addressLine1 || "");
    payload.append("address_line_2", draft.addressLine2 || "");
    payload.append("state", draft.state || "");
    payload.append("city", draft.city || "");
    payload.append("pincode", draft.pincode || "");
    // payload.append("pan_number", draft.panNumber || "");
    // payload.append("aadhaar_number", draft.aadhaarNumber || "");
    // payload.append("account_holder_name", draft.bankAccountHolderName || "");
    // payload.append("bank_name", draft.bankName || "");
    // payload.append("account_number", draft.accountNumber || "");
    // payload.append("ifsc_code", draft.ifscCode || "");

    if (draft.profilePhoto instanceof File) {
      payload.append("profile_photo", draft.profilePhoto);
    }

    // if (draft.idProofFile instanceof File) {
    //   payload.append("id_proof", draft.idProofFile);
    // }

    return payload;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please correct the highlighted errors.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildUpdatePayload();
      const response = await userUpdateProfile(profile.id, payload);
      const apiData = response?.data?.data || response?.data || {};

      const mappedProfile =
        apiData && Object.keys(apiData).length > 0
          ? mapApiProfileToState(apiData)
          : {
            ...profile,
            ...draft,
            kycStatus: hasKycSensitiveNameChange ? "PENDING" : profile.kycStatus,
          };

      const nextProfile = {
        ...profile,
        ...mappedProfile,
        referralNumber: mappedProfile.referralNumber || profile.referralNumber || draft.referralNumber,
        profilePhoto: mappedProfile.profilePhoto || profile.profilePhoto || draft.profilePhoto,
        // idProofPath: mappedProfile.idProofPath || profile.idProofPath || draft.idProofPath,
        idProofName: mappedProfile.idProofName || profile.idProofName || draft.idProofName,
        mobileNumber:
          mappedProfile.mobileNumber && /^\d{10}$/.test(mappedProfile.mobileNumber)
            ? mappedProfile.mobileNumber
            : draft.mobileNumber,
        // aadhaarNumber: mappedProfile.aadhaarNumber || draft.aadhaarNumber || profile.aadhaarNumber,
        // panNumber: mappedProfile.panNumber || draft.panNumber || profile.panNumber,
        bankAccountHolderName:
          mappedProfile.bankAccountHolderName ||
          draft.bankAccountHolderName ||
          profile.bankAccountHolderName,
        bankName: mappedProfile.bankName || draft.bankName || profile.bankName,
        accountNumber: mappedProfile.accountNumber || draft.accountNumber || profile.accountNumber,
        ifscCode: mappedProfile.ifscCode || draft.ifscCode || profile.ifscCode,
        customUserId: mappedProfile.customUserId || draft.customUserId || profile.customUserId,
        // businessGenerated: mappedProfile.businessGenerated || draft.businessGenerated || profile.businessGenerated,
        totalEarnings: mappedProfile.totalEarnings || draft.totalEarnings || profile.totalEarnings,
        // level: mappedProfile.level || draft.level || profile.level,
        walletBalance: mappedProfile.walletBalance || draft.walletBalance || profile.walletBalance,
      };

      setProfile(nextProfile);
      setDraft(nextProfile);
      setIsEditing(false);
      setProfilePreview(getResolvedImageSrc(nextProfile.profilePhoto));

      const updatedProfilePhoto =
        apiData?.profile_photo ||
        mappedProfile.profilePhoto ||
        profile.profilePhoto ||
        draft.profilePhoto ||
        "";

      if (updatedProfilePhoto) {
        sessionStorage.setItem(
          "authProfilePhoto",
          getResolvedImageSrc(updatedProfilePhoto)
        );
        window.dispatchEvent(new Event("authProfilePhotoUpdated"));
      }

      const message = hasKycSensitiveNameChange
        ? "Profile saved. KYC has been reactivated because the full name changed."
        : "Profile saved successfully.";

      // setAlertMessage(message);
      toast.success(message);
    } catch (error) {
      // console.error("Failed to save profile:", error);
      toast.error(error?.response?.data?.message || "Unable to save profile details.");
    } finally {
      setSaving(false);
    }
  };

  // 🔽 ADD
  const handleVerifyBank = async () => {
    if (!draft.accountNumber || !draft.ifscCode) {
      toast.error("Account number and IFSC code are required");
      return;
    }

    if (!kycId) {
      toast.error("KYC ID not found. Please do KYC first / re-login.");
      return;
    }

    try {
      setBankVerifying(true);

      const payload = {
        kyc_id: kycId,
        account_number: draft.accountNumber,
        ifsc: draft.ifscCode,
      };

      // console.log("Verifying bank with payload:", payload);


      const res = await verifyBankByPennyDrop(payload);
      const apiData = res?.data?.data || {};

      toast.success("Bank details verified successfully");

      // refresh profile to get updated verified bank data
      const refreshed = await userProfileData();
      const mapped = mapApiProfileToState(refreshed?.data?.data || {});
      setProfile(mapped);
      setDraft(mapped);
      setIsBankEditing(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Bank verification failed"
      );
    } finally {
      setBankVerifying(false);
    }
  };

  const renderValue = (value) =>
    value && String(value).trim().length > 0 ? value : "N/A" || "-";

  if (loading) {
    return (
      <>
        <div
          style={{
            overflowX: "hidden",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
            <div>
              <h2 className="h4 mb-1">My Profile</h2>
            </div>
          </div>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body className="p-4">Loading profile...</Card.Body>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        style={{
          overflowX: "hidden",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <div>
            <h2 className="h4 mb-1">My Profile</h2>
            {/* <div className="text-muted">View and update your personal, KYC, and bank details.</div> */}
          </div>

          <div className="d-flex gap-2">
            {!isEditing ? (
              <Button variant="primary" onClick={openEditMode} className="submit-btn">
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit Profile
              </Button>
            ) : (
              <>
                <Button onClick={cancelEdit} className="cancel-btn">
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  className="submit-btn"
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>

        {alertMessage ? (
          <Alert variant="info" className="border-0 shadow-sm rounded-4">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {alertMessage}
          </Alert>
        ) : null}

        <Row className="g-4 mx-0">
          <Col xs={12} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-4">
                <div className="text-center">
                  {/* <div className="position-relative d-inline-block mb-3">
                    <img
                      src={getResolvedImageSrc(profilePreview)}
                      alt="Profile"
                      className="rounded-circle border"
                      style={{ width: 120, height: 120, objectFit: "cover" }}
                    />
                    {isEditing ? (
                      <label
                        htmlFor="profilePhoto"
                        className="position-absolute bottom-0 end-0 btn btn-primary btn-sm rounded-circle shadow-sm"
                        style={{ width: 38, height: 38 }}
                      >
                        <FontAwesomeIcon icon={faCamera} className="mt-2" />
                      </label>
                    ) : null}
                  </div> */}

                  <div className="position-relative d-inline-block mb-3">
                    {profilePreview && !profileImageError ? (
                      <img
                        src={getResolvedImageSrc(profilePreview)}
                        alt="Profile"
                        className="rounded-circle border"
                        style={{ width: 120, height: 120, objectFit: "cover" }}
                        onError={() => setProfileImageError(true)}
                      />
                    ) : (
                      <div
                        className="rounded-circle border d-flex align-items-center justify-content-center"
                        style={{
                          width: 120,
                          height: 120,
                          background: "#f4f6f8",
                          color: "#b0b7c3",
                          fontSize: "3rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}

                    {isEditing ? (
                      <label
                        htmlFor="profilePhoto"
                        className="position-absolute bottom-0 end-0 btn btn-primary btn-sm rounded-circle shadow-sm"
                        style={{ width: 38, height: 38 }}
                      >
                        <FontAwesomeIcon icon={faCamera} className="mt-2" />
                      </label>
                    ) : null}
                  </div>

                  {isEditing ? (
                    <Form.Group className="mb-3 text-start">
                      <Form.Label className="fw-semibold">Profile Photo</Form.Label>
                      <Form.Control
                        ref={profilePhotoInputRef}
                        id="profilePhoto"
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleProfilePhotoChange}
                      />
                      {errors.profilePhoto ? (
                        <div className="text-danger small mt-1">{errors.profilePhoto}</div>
                      ) : null}
                    </Form.Group>
                  ) : null}

                  <h4 className="mb-1">{profile.fullName}</h4>
                  <div className="text-muted mb-3">{profile.emailId}</div>

                  <Badge
                    bg={KYC_STATUS_VARIANT(profile.kycStatus)}
                    className="px-3 py-2 rounded-pill"
                  >
                    {profile.kycStatus}
                  </Badge>

                  {/* {profile.kycStatus === "PENDING" ? (
                    <div className="mt-3 text-warning small">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      KYC will be rechecked after profile changes.
                    </div>
                  ) : null} */}
                </div>

                <hr className="my-4" />

                <div className="small text-muted">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Mobile</span>
                    <span className="text-dark">{maskMobileNumber(profile.mobileNumber)}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Aadhaar Number</span>
                    <span className="text-dark">{renderValue(profile.aadhaarNumber)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Pan Number</span>
                    <span className="text-dark">{renderValue(profile.panNumber)}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>User Id</span>
                    <span className="text-dark">{profile.customUserId}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Referral Number</span>
                    <span className="text-dark">{profile.referralNumber}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>State</span>
                    <span className="text-dark">{renderValue(profile.state)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>City</span>
                    <span className="text-dark">{renderValue(profile.city)}</span>
                  </div>

                  {/* <div className="d-flex justify-content-between mb-2">
                    <span>Business Generated</span>
                    <span className="text-dark">₹{profile.businessGenerated}</span>
                  </div> */}
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Earnings</span>
                    <span className="text-dark">₹{profile.totalEarnings}</span>
                  </div>
                  {/* <div className="d-flex justify-content-between">
                    <span>User Level</span>
                    <span className="text-dark">{profile.level}</span>
                  </div> */}
                  <div className="d-flex justify-content-between">
                    <span>Wallet Balance</span>
                    <span className="text-dark">₹{profile.walletBalance}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={8} style={{ minWidth: 0 }}>
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-1">Profile Summary</h5>
                    {/* <div className="text-muted small">Basic profile information</div> */}
                  </div>
                  <Badge
                    bg={KYC_STATUS_VARIANT(profile.kycStatus)}
                    className="px-3 py-2 rounded-pill"
                  >
                    KYC {profile.kycStatus}
                  </Badge>
                </div>

                <Row className="g-3 mx-0">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Full Name <span className="text-danger">*</span>
                      </Form.Label>
                      {isEditing ? (
                        <InputGroup
                          className={`flex-nowrap ${errors.fullName ? "invalid-input-group" : ""}`}
                        >
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faUser} />
                          </InputGroup.Text>
                          <Form.Control
                            name="fullName"
                            value={draft.fullName}
                            onChange={handleChange}
                            isInvalid={!!errors.fullName}
                            className="border-start-0"
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.fullName)}</div>
                      )}
                      {errors.fullName ? (
                        <div className="text-danger small mt-1">{errors.fullName}</div>
                      ) : null}
                      {isEditing && hasKycSensitiveNameChange ? (
                        <div className="text-warning small mt-1">
                          Changing the full name will reactivate the KYC process.
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Mobile Number <span className="text-danger">*</span>
                      </Form.Label>
                      {isEditing ? (
                        <InputGroup
                          className={`flex-nowrap ${errors.mobileNumber ? "invalid-input-group" : ""}`}
                        >
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faPhone} />
                          </InputGroup.Text>
                          <Form.Control
                            name="mobileNumber"
                            value={draft.mobileNumber}
                            onChange={handleMobileChange}
                            isInvalid={!!errors.mobileNumber}
                            maxLength={10}
                            className="border-start-0"
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">
                          {maskMobileNumber(profile.mobileNumber)}
                        </div>
                      )}
                      {errors.mobileNumber ? (
                        <div className="text-danger small mt-1">{errors.mobileNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Email ID <span className="text-danger">*</span>
                      </Form.Label>
                      {isEditing ? (
                        <InputGroup
                          className={`flex-nowrap ${errors.emailId ? "invalid-input-group" : ""}`}
                        >
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faEnvelope} />
                          </InputGroup.Text>
                          <Form.Control
                            name="emailId"
                            type="email"
                            value={draft.emailId}
                            onChange={handleChange}
                            isInvalid={!!errors.emailId}
                            className="border-start-0"
                            readOnly
                            style={{ background: "#f8f9fa" }}
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.emailId)}</div>
                      )}
                      {errors.emailId ? (
                        <div className="text-danger small mt-1">{errors.emailId}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="mb-4">Address Details</h5>
                {/* <div className="text-muted small mb-4">Primary and mailing address</div> */}

                <Row className="g-3 mx-0">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Address Line 1</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="addressLine1"
                          value={draft.addressLine1}
                          onChange={handleChange}
                          placeholder="Enter address line 1"
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {renderValue(profile.addressLine1)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Address Line 2</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="addressLine2"
                          value={draft.addressLine2}
                          onChange={handleChange}
                          placeholder="Enter address line 2"
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {renderValue(profile.addressLine2)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">State</Form.Label>
                      {isEditing ? (
                        <Form.Select name="state" value={draft.state} onChange={handleChange}>
                          <option value="">Select state</option>
                          {STATE_OPTIONS.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.state)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  {/* <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">City</Form.Label>
                    {isEditing ? (
                      <Form.Select name="city" value={draft.city} onChange={handleChange}>
                        <option value="">Select city</option>
                        {(selectedCities || []).map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="form-control bg-light">{renderValue(profile.city)}</div>
                    )}
                  </Form.Group>
                </Col> */}

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">City</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="city"
                          value={draft.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.city)}</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Pincode</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="pincode"
                          value={draft.pincode}
                          onChange={handlePincodeChange}
                          maxLength={6}
                          placeholder="Enter pincode"
                          isInvalid={!!errors.pincode}
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.pincode)}</div>
                      )}
                      {errors.pincode ? (
                        <div className="text-danger small mt-1">{errors.pincode}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>

                {isEditing ? (
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      className="submit-btn"
                      disabled={saving}
                    >
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      {saving ? "Updating..." : "Update Profile"}
                    </Button>
                  </div>
                ) : null}
              </Card.Body>
            </Card>

            {/* <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="mb-4">KYC Details</h5>
                <div className="text-muted small mb-4">Identity and document details</div>

                <Row className="g-3 mx-0">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">PAN Number</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="panNumber"
                          value={draft.panNumber}
                          onChange={handleChange}
                          placeholder="Enter PAN"
                          isInvalid={!!errors.panNumber}
                        />
                      ) : (
                        <div className="form-control bg-light">{maskPanNumber(profile.panNumber)}</div>
                      )}
                      {errors.panNumber ? (
                        <div className="text-danger small mt-1">{errors.panNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Aadhaar Number</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="aadhaarNumber"
                          value={draft.aadhaarNumber}
                          onChange={handleAadhaarChange}
                          placeholder="Enter Aadhaar number"
                          maxLength={12}
                          isInvalid={!!errors.aadhaarNumber}
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {maskAadhaarNumber(profile.aadhaarNumber)}
                        </div>
                      )}
                      {errors.aadhaarNumber ? (
                        <div className="text-danger small mt-1">{errors.aadhaarNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold d-block">KYC Status</Form.Label>
                      <Badge
                        bg={KYC_STATUS_VARIANT(profile.kycStatus)}
                        className="px-3 py-2 rounded-pill"
                      >
                        {profile.kycStatus}
                      </Badge>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">ID Proof Upload</Form.Label>
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleIdProofChange}
                          />
                          <div className="form-text">Allowed: PDF, JPG, PNG. Max size: 2 MB.</div>

                          {profile.idProofPath ? (
                            <div className="mt-2">
                              <Button
                                type="button"
                                variant="link"
                                className="p-0 text-decoration-none"
                                onClick={() =>
                                  window.open(
                                    normalizeFileUrl(profile.idProofPath),
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                              >
                                View Document
                              </Button>
                            </div>
                          ) : null}

                          {errors.idProof ? (
                            <div className="text-danger small mt-1">{errors.idProof}</div>
                          ) : null}
                        </>
                      ) : profile.idProofPath ? (
                        <div className="form-control bg-light d-flex justify-content-between align-items-center">
                          <span>{renderValue(profile.idProofName)}</span>
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() =>
                              window.open(
                                normalizeFileUrl(profile.idProofPath),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            View
                          </Button>
                        </div>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.idProofName)}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card> */}

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                {/* <h5 className="mb-4">Bank Details</h5> */}
                {/* <div className="text-muted small mb-4">Payout and settlement details</div> */}

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Bank Details</h5>

                  <div className="d-flex align-items-center gap-2">
                    <Badge
                      bg={KYC_STATUS_VARIANT(profile.bankVerificationStatus || "PENDING")}
                      className="px-3 py-2 rounded-pill"
                    >
                      {profile.bankVerificationStatus || "PENDING"}
                    </Badge>

                    {!isBankEditing && (
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => setIsBankEditing(true)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    )}
                  </div>
                </div>

                <Row className="g-3 mx-0">
                  {/* <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Account Holder Name</Form.Label>
                      {isBankEditing ? (
                        <Form.Control
                          name="bankAccountHolderName"
                          value={draft.bankAccountHolderName}
                          onChange={handleChange}
                          placeholder="Enter account holder name"
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {renderValue(profile.bankAccountHolderName)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Bank Name</Form.Label>
                      {isBankEditing ? (
                        <Form.Control
                          name="bankName"
                          value={draft.bankName}
                          onChange={handleChange}
                          placeholder="Enter bank name"
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {renderValue(profile.bankName)}
                        </div>
                      )}
                    </Form.Group>
                  </Col> */}

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Account Number</Form.Label>
                      {isBankEditing ? (
                        <Form.Control
                          name="accountNumber"
                          value={draft.accountNumber}
                          onChange={handleAccountNumberChange}
                          placeholder="Enter account number"
                          isInvalid={!!errors.accountNumber}
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {maskAccountNumber(profile.accountNumber)}
                        </div>
                      )}
                      {errors.accountNumber ? (
                        <div className="text-danger small mt-1">{errors.accountNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">IFSC Code</Form.Label>
                      {isBankEditing ? (
                        <Form.Control
                          name="ifscCode"
                          value={draft.ifscCode}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                          isInvalid={!!errors.ifscCode}
                          style={{ textTransform: "uppercase" }}
                        />
                      ) : (
                        <div className="form-control bg-light">{maskIfscCode(profile.ifscCode)}</div>
                      )}
                      {errors.ifscCode ? (
                        <div className="text-danger small mt-1">{errors.ifscCode}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>

                {isBankEditing && (
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button
                      onClick={() => {
                        setDraft(profile);
                        setIsBankEditing(false);
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="primary"
                      className="submit-btn"
                      onClick={handleVerifyBank}
                      disabled={bankVerifying}
                    >
                      {bankVerifying ? "Verifying..." : "Update & Verify"}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* <Modal show={showCropModal} onHide={closeCropModal} centered size="sm" backdrop="static">
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="fw-bold">Crop Profile Image</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <div className="crop-stage-wrapper">
            <div className="crop-stage">
              {cropSourcePreview ? (
                <img
                  src={cropSourcePreview}
                  alt="Crop preview"
                  className="crop-stage-image"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${cropRotation}deg) scale(${cropZoom})`,
                  }}
                />
              ) : null}
              <div className="crop-stage-grid" />
            </div>
          </div>

          <Row className="mt-4 g-3 align-items-center">
            <Col md={6}>
              <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} className="text-primary" />
                Zoom
              </Form.Label>
              <Form.Range
                min="1"
                max="3"
                step="0.01"
                value={cropZoom}
                onChange={(e) => setCropZoom(parseFloat(e.target.value))}
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faRotateRight} className="text-primary" />
                Rotate
              </Form.Label>
              <Form.Range
                min="-180"
                max="180"
                step="1"
                value={cropRotation}
                onChange={(e) => setCropRotation(parseInt(e.target.value, 10))}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={closeCropModal} disabled={cropping}>
            Cancel
          </Button>
          <Button style={{ ...KYC_STATUS_VARIANT ? ACTIVE_BUTTON_STYLE : ACTIVE_BUTTON_STYLE }} onClick={applyCroppedProfilePhoto} disabled={cropping}>
            {cropping ? "Saving Crop..." : "Save Crop"}
          </Button>
        </Modal.Footer>
      </Modal> */}

      <style>
        {`
  .submit-btn {
    background: linear-gradient(90deg, #2c60ba 0%, #005dfb 100%);
    border: none;
    color: #ffffff;
  },

  // .cancel-btn {
  //  background: #ffffff;
  //   border: 1px solid #d61515;
  //   color: #d61515;
  // }

  .crop-stage-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .crop-stage {
    position: relative;
    width: min(100%, 860px);
    aspect-ratio: 1 / 1;
    background: #0f172a;
    overflow: hidden;
    border-radius: 16px;
    border: 1px solid rgba(44, 96, 186, 0.18);
  }

  .crop-stage-image {
    position: absolute;
    top: 50%;
    left: 50%;
    width: auto;
    height: auto;
    max-width: none;
    max-height: none;
    transform-origin: center center;
    user-select: none;
    pointer-events: none;
  }

  .crop-stage-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px);
    background-size: 33.333% 33.333%;
    pointer-events: none;
    mix-blend-mode: screen;
  }

  .modal-content {
    border-radius: 18px;
  }

  .modal-header .btn-close {
    display: none;
  }

  @media (max-width: 767.98px) {
    .crop-stage {
      width: 100%;
      max-width: 100%;
    }
  }
`}
      </style>
    </>
  );
}